/**
 * HibpCheck — mic indicator care rulează debounced verificare HIBP
 * pe o parolă și afișează rezultatul.
 *
 * Rămâne complet client-side: doar SHA1[0..5] ajunge la api.pwnedpasswords.com
 * (vezi `crypto/hibp.ts`).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import { checkPasswordPwned, type HibpResult } from '@/crypto/hibp';

export default function HibpCheck({
  password,
  debounceMs = 800,
}: {
  password: string;
  debounceMs?: number;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'checking' }
    | { kind: 'result'; result: HibpResult }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  useEffect(() => {
    if (!password) {
      setState({ kind: 'idle' });
      return;
    }
    // `cancelled` e la nivelul efectului: un răspuns întârziat pentru o parolă
    // veche nu trebuie să suprascrie rezultatul parolei curente.
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setState({ kind: 'checking' });
      checkPasswordPwned(password)
        .then((result) => {
          if (!cancelled) setState({ kind: 'result', result });
        })
        .catch((e) => {
          if (!cancelled) {
            setState({
              kind: 'error',
              message: e instanceof Error ? e.message : i18n.t('vault_ui.hibp.error_fallback'),
            });
          }
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [password, debounceMs]);

  if (state.kind === 'idle') return null;
  if (state.kind === 'checking') {
    return (
      <div className="text-[10px] mt-1" style={{ color: 'var(--text-dim)' }}>
        {t('vault_ui.hibp.checking')}
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="text-[10px] mt-1" style={{ color: 'var(--text-dim)' }}>
        {t('vault_ui.hibp.unavailable', { message: state.message })}
      </div>
    );
  }

  const { count, pwned } = state.result;
  if (!pwned) {
    return (
      <div
        className="text-[10px] mt-1 flex items-center gap-1"
        style={{ color: 'var(--accent)' }}
      >
        <span>✓</span>
        <span>{t('vault_ui.hibp.clean')}</span>
      </div>
    );
  }
  return (
    <div
      className="text-[10px] mt-1 flex items-center gap-1 font-medium"
      style={{ color: 'var(--danger)' }}
    >
      <span>⚠</span>
      <span>{t('vault_ui.hibp.pwned', { n: formatCount(count) })}</span>
    </div>
  );
}

function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
